import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { FaceVerifyScreens } from '../constants/face.const';
import { cn } from '../lib/utils';
import ChangePhoneNumber from './ChangePhoneNumber';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';

interface IProps {
  phoneNumber: string;
  onClose: () => void;
  isOpen: boolean;
  onVerify?: (otp: string) => void;
  verifyError?: Error | null;
  screen?: FaceVerifyScreens;
  disableCannotAccessOldPhone?: boolean;
  /** True while the caller is acting on a submitted OTP; disables Verify. */
  isVerifying?: boolean;
}

const TIMEOUT = 60; // 60 seconds for OTP expiration

function maskPhoneNumber(input: string): string {
  const [prefix, number] = input.split('-');
  const maskedPrefix = '*'.repeat(prefix?.length);
  const maskedNumber = '*'.repeat(number?.length - 2) + number?.slice(-2);
  return `${maskedPrefix}-${maskedNumber}`;
}

const PhoneVerificationModal: React.FC<IProps> = ({
  phoneNumber,
  onClose,
  isOpen,
  onVerify,
  verifyError,
  screen = FaceVerifyScreens.UPDATE_PHONE,
  disableCannotAccessOldPhone = false,
  isVerifying = false
}) => {
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(TIMEOUT);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [prefix, number] = phoneNumber.split('-');

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isCountdownActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setIsCountdownActive(false);
    }
    return () => clearTimeout(timer);
  }, [isCountdownActive, countdown]);

  // Handle verifyError for OTP_FAILED_ATTEMPTS
  useEffect(() => {
    if (verifyError) {
      const errorCode = (
        verifyError as Error & {
          response?: { data?: { error?: { code?: string; message?: string } } };
        }
      )?.response?.data?.error?.code;
      const errorMessage = (
        verifyError as Error & {
          response?: { data?: { error?: { code?: string; message?: string } } };
        }
      )?.response?.data?.error?.message;

      if (errorCode === 'OTP_FAILED_ATTEMPTS') {
        setCountdown(0);
        setIsCountdownActive(false);
        setErrorMessage(
          errorMessage || 'Too many failed attempts. Please try again later.'
        );
      }
    }
  }, [verifyError]);

  const requestOtp = useMutation({
    mutationKey: ['requestOtp'],
    mutationFn: async ({
      number,
      prefix
    }: {
      number: string;
      prefix: string;
    }) => {
      await apiClient.post('/auth/get-otp', {
        phoneNumber: number,
        countryCode: prefix,
        eventType: 'phone_verify'
      });
    },
    onSuccess: () => {
      setIsCountdownActive(true);
      setErrorMessage('');
      setCountdown(TIMEOUT);
      setOtp(''); // Clear OTP input on success
      console.log('OTP requested successfully');
    },
    onError: (error) => {
      console.error('Error requesting OTP:', error);
      setErrorMessage(
        (
          error as Error & {
            response?: { data?: { error?: { message?: string } } };
          }
        )?.response?.data?.error?.message ||
          'Failed to request OTP. Please try again.'
      );
      setIsCountdownActive(false);
      setCountdown(0);
      setOtp(''); // Clear OTP input on error
    }
  });

  useEffect(() => {
    if (isOpen) {
      requestOtp.mutate({ number, prefix });
    }
  }, [isOpen]); // adding number, prefix deps will cause infinite api calls

  const handleVerify = () => {
    onVerify?.(otp);
    setOtp('');
  };

  const handleResend = () => {
    if (countdown === 0) {
      requestOtp.mutate({ number, prefix });
    }
  };

  const handleCloseDialog = () => {
    onClose();
    setOtp('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseDialog}>
      <DialogContent
        className="max-w-[24rem] rounded-3xl"
        aria-describedby="phone-verification-modal"
      >
        <DialogHeader className="mt-10">
          <DialogTitle className="text-center">
            Mobile Phone Verification
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center">
          <p className="text-md my-2 text-center">One Time Password</p>
          {/* Add verification code input here */}
          <InputOTP maxLength={6} value={otp} onChange={setOtp}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={1} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={3} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={4} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <p
          className={cn(
            'mt-4 text-center text-xs',
            errorMessage ? 'text-[#FF5E18]' : 'text-[#aac4b9]'
          )}
        >
          {errorMessage ||
            `A OTP (One Time Passcode) has been sent to ${maskPhoneNumber(phoneNumber)}. Please enter the OTP in the field above to verify your phone.`}
        </p>

        {isCountdownActive && countdown > 0 && (
          <p className="mt-2 text-center text-sm">
            Resend OTP in <strong>{countdown}s</strong>
          </p>
        )}

        {isCountdownActive && countdown > 0 ? (
          <Button
            className="mt-3 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
            onClick={() => handleVerify()}
            disabled={otp.length !== 6 || isVerifying}
            type="button"
          >
            Verify
          </Button>
        ) : (
          <Button
            variant={'outline'}
            className="text-md mt-3 rounded-full border-2 border-[#5CB891] bg-white font-semibold text-[#5CB891] hover:bg-white hover:text-[#5CB891]"
            onClick={() => handleResend()}
            type="button"
          >
            Resend OTP
          </Button>
        )}

        {/* {timer === 0 && <ChangePhoneNumber />} */}
        {!disableCannotAccessOldPhone && <ChangePhoneNumber screen={screen} />}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneVerificationModal;
